import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RenderingClient from "../../clients/RenderingClient";
import { useRouteState } from "../../hooks/useRouteState";
import { useAuth } from "../../hooks/useAuth";
import { ROUTE_TYPES } from "../constants/routeConstants";
import { useUser } from "./UserContext";

const RemoteRenderContext = createContext();

// Custom event name for connection errors (can be used by NotificationContext consumers)
export const REMOTE_RENDER_CONNECTION_ERROR_EVENT =
  "remote-render:connection-error";

export const RemoteRenderProvider = ({ children }) => {
  const clientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);
  const [surgerySessionVersions, setSurgerySessionVersions] = useState({});
  const [lastSurgeryViews, setLastSurgeryViews] = useState({});

  const { routeType } = useRouteState();
  const { userPreferences } = useUser();
  const { isAuthenticated, loading: isAuthLoading } = useAuth();

  const zoomSensitivity =
    userPreferences?.display_settings?.float_sensitivity ??
    RenderingClient.DEFAULT_ZOOM_SENSITIVITY;

  const digitalTwinViewCacheRef = useRef(new Map());
  const digitalTwinCreationViewCacheRef = useRef(new Map());
  const digitalTwinSurgeryCacheRef = useRef(new Map());

  const REMOTE_RENDER_ROUTES = useMemo(
    () => [
      ROUTE_TYPES.DIGITAL_TWIN,
      ROUTE_TYPES.VALIDATION,
      ROUTE_TYPES.CHECK,
      ROUTE_TYPES.ALIGN,
    ],
    []
  );

  const initializeClient = useCallback(async () => {
    if (isLoading || isConnected || hasExhaustedRetries) return;
    setIsLoading(true);
    try {
      if (clientRef.current) {
        await clientRef.current.endRemoteSession();
      }
      clientRef.current = new RenderingClient({
        zoomSensitivity,
      });

      // Set up connection error listener
      clientRef.current.addConnectionErrorListener(errorInfo => {
        // Dispatch custom event for components that want to show notifications
        window.dispatchEvent(
          new CustomEvent(REMOTE_RENDER_CONNECTION_ERROR_EVENT, {
            detail: errorInfo,
          })
        );
      });

      await clientRef.current.connectAsync();
      setIsConnected(true);
      setError(null);
      setHasExhaustedRetries(false);
    } catch (err) {
      setError(err);
      // Check if retries are exhausted (notification already dispatched by RenderingClient)
      if (err.retriesExhausted || clientRef.current?.hasExhaustedRetries) {
        setHasExhaustedRetries(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, isLoading, hasExhaustedRetries, zoomSensitivity]);

  // Global cache operations that work across all caches
  const clearAllCaches = useCallback(() => {
    digitalTwinViewCacheRef.current.clear();
    digitalTwinCreationViewCacheRef.current.clear();
    digitalTwinSurgeryCacheRef.current.clear();
  }, []);

  const disconnectClient = useCallback(async () => {
    if (!clientRef.current || !isConnected) return;
    try {
      await clientRef.current.endRemoteSession();
    } catch (err) {
    } finally {
      clientRef.current = null;
      setIsConnected(false);
      clearAllCaches();
      setSurgerySessionVersions({});
      setLastSurgeryViews({});
    }
  }, [isConnected, clearAllCaches]);

  const createCacheOperations = useCallback(
    cacheRef => ({
      hasView: cacheKey => cacheRef.current.has(cacheKey),
      getView: cacheKey => cacheRef.current.get(cacheKey),
      setView: (cacheKey, viewData) => cacheRef.current.set(cacheKey, viewData),
      clear: () => cacheRef.current.clear(),
      removeView: cacheKey => cacheRef.current.delete(cacheKey),
      getSize: () => cacheRef.current.size,
      clearForPatient: patientId => {
        const keysToDelete = [];
        cacheRef.current.forEach((_, key) => {
          if (key.includes(patientId)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach(key => cacheRef.current.delete(key));
      },
      clearForTwin: twinId => {
        const keysToDelete = [];
        cacheRef.current.forEach((_, key) => {
          if (key.endsWith(`_${twinId}`)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach(key => cacheRef.current.delete(key));
      },
      getAllKeys: () => Array.from(cacheRef.current.keys()),
      getAllEntries: () => Array.from(cacheRef.current.entries()),
    }),
    []
  );

  // SOLUTION: Memoize the cache operations objects
  const digitalTwinCache = useMemo(
    () => createCacheOperations(digitalTwinViewCacheRef),
    [createCacheOperations]
  );

  const digitalTwinCreationCache = useMemo(
    () => createCacheOperations(digitalTwinCreationViewCacheRef),
    [createCacheOperations]
  );

  const digitalTwinSurgeryCache = useMemo(
    () => createCacheOperations(digitalTwinSurgeryCacheRef),
    [createCacheOperations]
  );

  useEffect(() => {
    if (!clientRef.current) return;
    const unsubscribe = clientRef.current.addSurgeryReloadListener(
      ({ key, step, result }) => {
        const effectiveStep = step || key || "unknown";

        // ONLY update last views - cache lifecycle is managed explicitly by components
        setLastSurgeryViews(prev => ({
          ...prev,
          [effectiveStep]: result,
        }));
      }
    );
    return unsubscribe;
  }, [isConnected]);

  const clearAllCachesForPatient = useCallback(patientId => {
    [
      digitalTwinViewCacheRef,
      digitalTwinCreationViewCacheRef,
      digitalTwinSurgeryCacheRef,
    ].forEach(cacheRef => {
      const keysToDelete = [];
      cacheRef.current.forEach((_, key) => {
        if (key.includes(patientId)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cacheRef.current.delete(key));
    });
  }, []);

  const clearAllCachesForTwin = useCallback(twinId => {
    [
      digitalTwinViewCacheRef,
      digitalTwinCreationViewCacheRef,
      digitalTwinSurgeryCacheRef,
    ].forEach(cacheRef => {
      const keysToDelete = [];
      cacheRef.current.forEach((_, key) => {
        if (key.endsWith(`_${twinId}`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cacheRef.current.delete(key));
    });
  }, []);

  const getTotalCacheSize = useCallback(() => {
    return (
      digitalTwinViewCacheRef.current.size +
      digitalTwinCreationViewCacheRef.current.size +
      digitalTwinSurgeryCacheRef.current.size
    );
  }, []);

  const getCacheStats = useCallback(() => {
    return {
      visualization: {
        size: digitalTwinViewCacheRef.current.size,
        keys: Array.from(digitalTwinViewCacheRef.current.keys()),
      },
      creation: {
        size: digitalTwinCreationViewCacheRef.current.size,
        keys: Array.from(digitalTwinCreationViewCacheRef.current.keys()),
      },
      surgery: {
        size: digitalTwinSurgeryCacheRef.current.size,
        keys: Array.from(digitalTwinSurgeryCacheRef.current.keys()),
      },
      total:
        digitalTwinViewCacheRef.current.size +
        digitalTwinCreationViewCacheRef.current.size +
        digitalTwinSurgeryCacheRef.current.size,
    };
  }, []);

  // Memoize global cache operations as well
  const globalCache = useMemo(
    () => ({
      clearAll: clearAllCaches,
      clearAllForPatient: clearAllCachesForPatient,
      clearAllForTwin: clearAllCachesForTwin,
      getTotalSize: getTotalCacheSize,
      getStats: getCacheStats,
    }),
    [
      clearAllCaches,
      clearAllCachesForPatient,
      clearAllCachesForTwin,
      getTotalCacheSize,
      getCacheStats,
    ]
  );

  // Manual retry function - resets retry state and attempts connection again
  const retryConnection = useCallback(async () => {
    if (clientRef.current) {
      clientRef.current.resetRetryState();
    }
    setHasExhaustedRetries(false);
    setError(null);
    // Wait for next tick to ensure state is updated
    await new Promise(resolve => setTimeout(resolve, 0));
    await initializeClient();
  }, [initializeClient]);

  // Memoize the entire context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      client: isConnected ? clientRef.current : null,
      isConnected,
      isLoading,
      error,
      hasExhaustedRetries,
      initializeClient,
      disconnectClient,
      retryConnection,

      // Stable cache instances
      digitalTwinCache,
      digitalTwinCreationCache,
      digitalTwinSurgeryCache,
      lastSurgeryViews,
      surgerySessionVersions,

      // Global cache operations
      globalCache,
    }),
    [
      isConnected,
      isLoading,
      error,
      hasExhaustedRetries,
      initializeClient,
      disconnectClient,
      retryConnection,
      digitalTwinCache,
      digitalTwinCreationCache,
      digitalTwinSurgeryCache,
      lastSurgeryViews,
      surgerySessionVersions,
      globalCache,
    ]
  );

  useEffect(() => {
    if (!clientRef.current) {
      return;
    }

    clientRef.current.setRenderingOptions({ zoomSensitivity });
    if (isConnected) {
      clientRef.current.setZoomSensitivity(zoomSensitivity).catch(() => {});
    }
  }, [zoomSensitivity, isConnected]);

  useEffect(() => {
    return () => {
      disconnectClient();
    };
  }, [disconnectClient]);

  // Auto-connect/disconnect based on route type
  useEffect(() => {
    const shouldBeConnected = REMOTE_RENDER_ROUTES.includes(routeType);
    const canInitializeConnection = !isAuthLoading && isAuthenticated;

    if (shouldBeConnected && canInitializeConnection && !isConnected && !isLoading) {
      initializeClient();
    } else if (!shouldBeConnected && isConnected) {
      disconnectClient();
    }
  }, [
    routeType,
    isAuthenticated,
    isAuthLoading,
    isConnected,
    isLoading,
    REMOTE_RENDER_ROUTES,
    initializeClient,
    disconnectClient,
  ]);

  return (
    <RemoteRenderContext.Provider value={contextValue}>
      {children}
    </RemoteRenderContext.Provider>
  );
};

export const useRemoteRenderContext = () => {
  const ctx = useContext(RemoteRenderContext);
  if (!ctx) {
    throw new Error("useRemoteRender must be used within RemoteRenderProvider");
  }
  return ctx;
};
